import { coreApi } from '@cp/shared/api/core';
import { apiRouteMethodDict } from '@common/api/routeMethod';
import { ApiRoute } from '@common/api/route';
import { generateApiUrl } from '@common/api/configure';
import {
  AdminPersonalQualitiesAiAnalysisModel,
  AdminPersonalQualitiesViewModel,
  AdminSavePersonalQualitiesAiAnalysisRequest,
} from '@common/model/admin/personalQualities';
import { ApiServerEntityIDType } from '@cp/utils/apiServer/types';

export enum AdminPersonalQualitiesApiTag {
  PersonalQualities = 'AdminPersonalQualities',
}

export const adminPersonalQualitiesApi = coreApi
  .enhanceEndpoints({ addTagTypes: [AdminPersonalQualitiesApiTag.PersonalQualities] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getPersonalQualities: builder.query<AdminPersonalQualitiesViewModel, { clientId: ApiServerEntityIDType }>({
        query: ({ clientId }) => ({
          method: apiRouteMethodDict[ApiRoute.AdminClientPersonalQualitiesGet],
          url: generateApiUrl(ApiRoute.AdminClientPersonalQualitiesGet, { clientId }),
        }),
        providesTags: (_res, _err, args) => [{ type: AdminPersonalQualitiesApiTag.PersonalQualities, id: args.clientId }],
      }),
      analyzePersonalQualities: builder.mutation<AdminPersonalQualitiesViewModel, { clientId: ApiServerEntityIDType }>({
        query: ({ clientId }) => ({
          method: apiRouteMethodDict[ApiRoute.AdminClientPersonalQualitiesAnalyze],
          url: generateApiUrl(ApiRoute.AdminClientPersonalQualitiesAnalyze, { clientId }),
        }),
        async onQueryStarted({ clientId }, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            dispatch(adminPersonalQualitiesApi.util.updateQueryData('getPersonalQualities', { clientId }, () => data));
          } catch {
            /* keep previous cache on error */
          }
        },
        invalidatesTags: (_res, _err, args) => [{ type: AdminPersonalQualitiesApiTag.PersonalQualities, id: args.clientId }],
      }),
      generatePersonalQualitiesAnalysis: builder.mutation<
        AdminPersonalQualitiesAiAnalysisModel,
        { clientId: ApiServerEntityIDType }
      >({
        query: ({ clientId }) => ({
          method: apiRouteMethodDict[ApiRoute.AdminClientPersonalQualitiesGenerateAnalysis],
          url: generateApiUrl(ApiRoute.AdminClientPersonalQualitiesGenerateAnalysis, { clientId }),
        }),
      }),
      savePersonalQualitiesAnalysis: builder.mutation<
        AdminPersonalQualitiesViewModel,
        { clientId: ApiServerEntityIDType; analysis: AdminSavePersonalQualitiesAiAnalysisRequest }
      >({
        query: ({ clientId, analysis }) => ({
          method: apiRouteMethodDict[ApiRoute.AdminClientPersonalQualitiesSaveAnalysis],
          url: generateApiUrl(ApiRoute.AdminClientPersonalQualitiesSaveAnalysis, { clientId }),
          body: analysis,
        }),
        async onQueryStarted({ clientId }, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            dispatch(adminPersonalQualitiesApi.util.updateQueryData('getPersonalQualities', { clientId }, () => data));
          } catch {
            /* keep previous cache on error */
          }
        },
        invalidatesTags: (_res, _err, args) => [{ type: AdminPersonalQualitiesApiTag.PersonalQualities, id: args.clientId }],
      }),
      deletePersonalQualitiesAnalysis: builder.mutation<AdminPersonalQualitiesViewModel, { clientId: ApiServerEntityIDType }>({
        query: ({ clientId }) => ({
          method: apiRouteMethodDict[ApiRoute.AdminClientPersonalQualitiesDeleteAnalysis],
          url: generateApiUrl(ApiRoute.AdminClientPersonalQualitiesDeleteAnalysis, { clientId }),
        }),
        async onQueryStarted({ clientId }, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            dispatch(adminPersonalQualitiesApi.util.updateQueryData('getPersonalQualities', { clientId }, () => data));
          } catch {
            /* keep previous cache on error */
          }
        },
        invalidatesTags: (_res, _err, args) => [{ type: AdminPersonalQualitiesApiTag.PersonalQualities, id: args.clientId }],
      }),
    }),
  });
